/**
 * Post-passphrase upsell: offer to enroll a platform authenticator
 * (Touch ID / Face ID / Windows Hello / Android biometric / PIN) so
 * subsequent unlocks can skip the passphrase entry. Strictly
 * optional — the passphrase stays the primary unlock path and the
 * only way to recover if the biometric credential is lost.
 *
 * This component appears once, between "set your passphrase" and
 * "signed in", only when `isWebAuthnPrfAvailable` returned true.
 */
import { useState } from 'react';

export interface BiometricEnrollPromptProps {
  readonly onEnroll: () => Promise<void>;
  readonly onSkip: () => void;
}

export function BiometricEnrollPrompt({ onEnroll, onSkip }: BiometricEnrollPromptProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function handleEnroll() {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await onEnroll();
    } catch (err) {
      // If enrollment fails (user cancels biometric prompt, platform
      // lacks PRF, etc.) we stay on this screen with an error. The
      // user can retry or skip.
      const raw = err instanceof Error ? err.message : 'enrollment failed';
      // LOW-V3-01: the platform-authenticator feature check doesn't
      // signal PRF support specifically, so browsers without PRF
      // (Firefox, Safari < 18) only discover the gap at enroll time.
      // Translate the internal string to something users can act on.
      setError(
        /does not support PRF|PRF probe failed/i.test(raw)
          ? 'Your browser does not yet support biometric unlock — please use your passphrase.'
          : raw,
      );
      setBusy(false);
    }
  }

  return (
    <main
      data-testid="biometric-enroll-prompt"
      style={{ maxWidth: 480, margin: '3em auto', padding: '0 1em' }}
    >
      <h2>enable biometric unlock?</h2>
      <p style={{ color: '#666', fontSize: '0.9em' }}>
        Your passphrase still works and stays the recovery path. If you enable biometrics, future
        unlocks on this device can use Touch ID, Face ID, Windows Hello, or your device PIN instead
        of typing the passphrase each time. The biometric secret never leaves your device.
      </p>
      {error && (
        <p data-testid="biometric-enroll-error" style={{ color: '#c00', fontSize: '0.9em' }}>
          {error}
        </p>
      )}
      <div style={{ display: 'flex', gap: '0.5em', marginTop: '1em' }}>
        <button
          type="button"
          data-testid="biometric-enroll-yes"
          disabled={busy}
          onClick={() => void handleEnroll()}
        >
          {busy ? 'prompting…' : 'Enable biometric unlock'}
        </button>
        <button type="button" data-testid="biometric-enroll-skip" disabled={busy} onClick={onSkip}>
          Skip
        </button>
      </div>
    </main>
  );
}
